import { v4 as uuid } from 'uuid';

// New Places API client class
export default class GooglePlacesAPI {
  // `types` describes the place itself. Without it a country is indistinguishable
  // from an address whose components could not be read: both arrive as blanks.
  STANDARD_FIELD_MASK = 'addressComponents,location,formattedAddress,types';

  constructor() {
    this.baseUrl = 'https://places.googleapis.com/v1';
    this.apiKey = null;
  }

  setApiKey(apiKey) {
    this.apiKey = apiKey;
  }

  // Autocomplete (New Places API)
  // `signal` is pulled out of the options: the rest are request-body fields, and
  // an AbortSignal serializes to `{}` rather than failing loudly.
  async autocomplete(input, options = {}) {
    const { signal, ...bodyOptions } = options;

    const response = await fetch(`${this.baseUrl}/places:autocomplete`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': this.apiKey,
      },
      body: JSON.stringify({
        input,
        languageCode: options.languageCode || 'en',
        regionCode: options.regionCode || 'US',
        sessionToken: options.sessionToken,
        ...bodyOptions,
      }),
    });

    if (!response.ok) {
      throw new Error(`Places API error: ${response.status}`);
    }

    const data = await response.json();

    // Normalize payload for queryPrediction.text which can be either a string or an object { text }
    try {
      if (data && Array.isArray(data.suggestions)) {
        const normalized = data.suggestions.map((sugg) => {
          const qp = sugg && sugg.queryPrediction;
          if (qp && qp.text && typeof qp.text === 'object' && typeof qp.text.text === 'string') {
            return {
              ...sugg,
              queryPrediction: {
                ...qp,
                text: qp.text.text,
              },
            };
          }
          return sugg;
        });
        return { ...data, suggestions: normalized };
      }
    } catch (e) {
      // If normalization fails, fall back to original data to avoid breaking callers
      return data;
    }

    return data;
  }

  // Get Place Details (New Places API)
  async getPlaceDetails(placeId, options = {}) {
    const { sessionToken, signal } = options;
    const url = new URL(`${this.baseUrl}/places/${placeId}`);

    // Add session token as query parameter if provided
    if (sessionToken) {
      url.searchParams.append('sessionToken', sessionToken);
    } else {
      console.warn('GooglePlacesAPI: Session token is missing in getPlaceDetails request. This may affect billing optimization.');
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      signal,
      headers: {
        'X-Goog-Api-Key': this.apiKey,
        'X-Goog-FieldMask': this.STANDARD_FIELD_MASK,
      },
    });

    if (!response.ok) {
      throw new Error(`Places API error: ${response.status}`);
    }

    return response.json();
  }

  // Generate session token for billing optimization
  static generateSessionToken() {
    return uuid();
  }
}
